import { Component, ViewEncapsulation, OnInit, OnDestroy } from "@angular/core";
import { Title } from "@angular/platform-browser";
import { ActivatedRoute } from "@angular/router";

import { select, Store } from "@ngrx/store";
import { Observable, Subscription, zip } from "rxjs";
import { map, skip } from "rxjs/operators";

import {
  getExcelProductGroupList,
  getProductGroupList,
  getProductGroupTableConfig,
  resetProductGroupsState,
  saveProductGroupTableConfig,
} from "src/app/store/actions/productGroups/productGroup.actions";
import {
  AppState,
  loadedCategoriesListPartyState,
  loadedMenusState,
  loadedProductGroupExcelListState,
  loadedProductGroupIsLoading,
  loadedProductGroupsListState,
  loadedProductGroupTableConfig,
  loadedProductGroupTotal,
  loadedPropertiesListPartyState,
} from "src/app/store/reducers";

import {
  ColumnSorterInfo,
  ConfigScope,
  ConfigSettingType,
  DefaultPage,
  ExportEvent,
  FieldType,
  FilterEvent,
  FormatType,
  PageEvent,
  SearchEvent,
  SortEvent,
  TableConfig,
  TableEvent,
} from "src/app/models/custom-table/custom-table.model";
import { MenuDTO, RouteData } from "src/app/models/Menu/MenuDTO";
import { ProductGroupExtendedDTO } from "src/app/models/Products/productGroupDTO";

import { getColorByBgColor } from "src/app/shared/utils/colorUtils";
import { subMenusIterator } from "src/app/shared/utils/menuUtils";
import { CategoryDTO } from "src/app/models/Categories/categoryDTO";
import { PropertyDTO } from "src/app/models/Properties/propertyDTO";
import {
  setCategories,
  setColumnInfoCategoriesProperties,
  setProperties,
} from "src/app/shared/utils/tableUtils";
import {
  getCategoriesForListByUsage,
  getPropertiesForListByUsage,
} from "src/app/store/actions/parties/parties.actions";

const USAGE_CODE = "PRODUCT_GROUP";

@Component({
  selector: "app-product-group-list",
  templateUrl: "./product-group-list.component.html",
  styleUrls: ["./product-group-list.component.scss"],
})
export class ProductGroupListComponent implements OnInit, OnDestroy {
  isLoading$: Observable<boolean>;

  columnInfo$: Observable<ColumnSorterInfo[]>;

  productGroupsList$: Observable<ProductGroupExtendedDTO[]>;
  excelProductGroupsList$: Observable<ProductGroupExtendedDTO[]>;
  totalProductGroups$: Observable<number>;
  multiSubs: Subscription;

  productGroupSubs: Subscription;
  productGroupsList: ProductGroupExtendedDTO[];

  categories$: Observable<CategoryDTO[]>;
  properties$: Observable<PropertyDTO[]>;

  menus$: Observable<MenuDTO[]>;
  menuSub: Subscription;

  formPath: string = "groups/group";
  tableTitle: string;
  headerIcon: string;

  tableConfig: TableConfig[] = [
    {
      header: "CustomGrid.Options",
      field: FieldType.OPTIONS,
      fieldType: FieldType.OPTIONS,
    },
    {
      header: "ProductGroup.code",
      field: "code",
      fieldType: FieldType.STRING,
      identifier: true,
      searchable: true,
    },
    {
      header: "ProductGroup.enabled",
      field: "enabled",
      fieldType: FieldType.BOOLEAN,
    },
    {
      header: "ProductGroup.description",
      field: "description",
      fieldType: FieldType.STRING,
      searchable: true,
    },
    {
      header: "ProductGroup.color",
      field: "color",
      fieldType: FieldType.CHIP_STRING,
    },
    {
      header: "ProductGroup.imageUrl",
      field: "imageUrl",
      fieldType: FieldType.IMAGE,
    },
    {
      header: "ProductGroup.createdOn",
      field: "createdOn",
      fieldType: FieldType.DATE_TIME,
      formatType: FormatType.DATETIME,
    },
    {
      header: "ProductGroup.updatedOn",
      field: "updatedOn",
      fieldType: FieldType.DATE_TIME,
      formatType: FormatType.DATETIME,
    },
  ];

  constructor(
    private store: Store<AppState>,
    private titleService: Title,
    private route: ActivatedRoute
  ) {
    this.isLoading$ = this.store.pipe(select(loadedProductGroupIsLoading));
    this.productGroupsList$ = this.store.pipe(
      select(loadedProductGroupsListState)
    );
    this.excelProductGroupsList$ = this.store.pipe(
      select(loadedProductGroupExcelListState)
    );
    this.categories$ = this.store.pipe(select(loadedCategoriesListPartyState));
    this.properties$ = this.store.pipe(select(loadedPropertiesListPartyState));

    this.totalProductGroups$ = this.store.pipe(select(loadedProductGroupTotal));
    this.columnInfo$ = this.store
      .pipe(select(loadedProductGroupTableConfig))
      .pipe(
        map((columnInfo) => {
          if (columnInfo) {
            return setColumnInfoCategoriesProperties(
              columnInfo,
              this.tableConfig
            );
          }
        })
      );
  }

  ngOnInit(): void {
    this.menus$ = this.store.pipe(select(loadedMenusState));

    this.loadScreenTitle();
    this.getProductGroupCategoriesProperties();
  }

  loadScreenTitle() {
    this.menuSub = this.menus$.subscribe((menus: MenuDTO[]) => {
      if (menus && menus.length) {
        this.route.data.subscribe((routeData: RouteData) => {
          menus.forEach((menuEl: MenuDTO) => {
            const selectedMenu = subMenusIterator(menuEl, routeData);
            if (selectedMenu) {
              this.headerIcon = selectedMenu.icon;
              this.tableTitle = selectedMenu.description;
              this.titleService.setTitle(this.tableTitle);
            }
          });
        });
      }
    });
  }

  setTableCustomElements() {
    this.productGroupSubs = this.productGroupsList$
      .pipe(skip(1))
      .subscribe((productGroups: ProductGroupExtendedDTO[]) => {
        this.productGroupsList = productGroups;

        if (this.productGroupsList && this.productGroupsList?.length) {
          this.productGroupsList = this.productGroupsList.map(
            (productGroup) => {
              let treatedProductGroup = productGroup;
              treatedProductGroup =
                this.setCustomProductChip(treatedProductGroup);
              return treatedProductGroup;
            }
          );
        }
      });
  }

  getProductGroupCategoriesProperties() {
    this.multiSubs = zip(
      this.categories$.pipe(skip(1)),
      this.properties$.pipe(skip(1))
    ).subscribe(([categories, properties]) => {
      if (categories && categories?.length) {
        const tableConfigCategories = setCategories(categories);
        if (tableConfigCategories) {
          this.tableConfig = this.tableConfig.concat(tableConfigCategories);
        }
      }

      if (properties && properties?.length) {
        const tableConfigProperties = setProperties(properties);
        if (tableConfigProperties) {
          this.tableConfig = this.tableConfig.concat(tableConfigProperties);
        }
      }

      this.getTableConfig();
      this.getProductGroups();
      this.setTableCustomElements();
    });

    this.store.dispatch(
      getCategoriesForListByUsage({
        pageReq: null,
        usageCode: USAGE_CODE,
      })
    );

    this.store.dispatch(
      getPropertiesForListByUsage({
        pageReq: null,
        usageCode: USAGE_CODE,
      })
    );
  }

  setCustomProductChip(productGroup: any) {
    const defaultProduct = {
      ...productGroup,
    };

    const { color } = productGroup;

    if (!color) {
      return defaultProduct;
    }

    let bgColor = getColorByBgColor(`#${color}`);
    let productGroupColor = {
      ...productGroup,
      color: {
        value: color,
        icon: "format_color_fill",
        style: `background-color: #${color}; color: ${bgColor};`,
      },
    };
    return productGroupColor;
  }

  getProductGroups(
    pageEvent?: PageEvent,
    sortEvent?: SortEvent,
    searchEvent?: SearchEvent,
    filterEvent?: FilterEvent
  ) {
    const { pageIndex, pageSize } = pageEvent || {};
    this.store.dispatch(
      getProductGroupList({
        options: {
          paging: {
            skip: (pageIndex * pageSize) | DefaultPage.PAGE_INDEX,
            top: pageSize ?? DefaultPage.PAGE_SIZE,
            realSize: true,
          },
          filteringArgs: searchEvent,
          sortingArgs: sortEvent,
          advancedFilteringArgs: filterEvent,
        },
      })
    );
  }

  getProductGroupExcelList(
    pageEvent?: PageEvent,
    sortEvent?: SortEvent,
    searchEvent?: SearchEvent,
    filterEvent?: FilterEvent
  ) {
    const { pageIndex, pageSize } = pageEvent || {};
    this.store.dispatch(
      getExcelProductGroupList({
        options: {
          paging: {
            skip: (pageIndex * pageSize) | DefaultPage.PAGE_INDEX,
            top: pageSize ?? DefaultPage.EXCEL_PAGE_SIZE,
          },
          filteringArgs: searchEvent,
          sortingArgs: sortEvent,
          advancedFilteringArgs: filterEvent,
        },
      })
    );
  }

  getTableConfig() {
    this.store.dispatch(
      getProductGroupTableConfig({
        options: {
          code: USAGE_CODE,
          settingType: ConfigSettingType.TABLE_CONFIG,
        },
      })
    );
  }

  onGetTableData(tableEvent: TableEvent) {
    const { pageEvent, sortEvent, searchEvent, filterEvent } = tableEvent;
    this.getProductGroups(pageEvent, sortEvent, searchEvent, filterEvent);
  }

  onGetExportData(exportEvent: ExportEvent) {
    const { pageEvent, sortEvent, searchEvent, filterEvent } = exportEvent;
    this.getProductGroupExcelList(pageEvent, sortEvent, searchEvent, filterEvent);
  }

  onSaveTableConfig(tableColumnInfo: ColumnSorterInfo[]) {
    this.store.dispatch(
      saveProductGroupTableConfig({
        config: {
          code: USAGE_CODE,
          settingTypeCode: ConfigSettingType.TABLE_CONFIG,
          scope: ConfigScope.USER,
          value: { tableColumnInfo },
        },
      })
    );
  }

  ngOnDestroy(): void {
    if (this.productGroupSubs) {
      this.productGroupSubs.unsubscribe();
    }
    if (this.multiSubs) {
      this.multiSubs.unsubscribe();
    }
    if (this.menuSub) {
      this.menuSub.unsubscribe();
    }
    this.store.dispatch(resetProductGroupsState());
  }
}
